import { IRynReplayEnvelope } from './ryn-replay-batcher';
import { RYN_REPLAY_FULL_SNAPSHOT_EVENT_TYPE } from './ryn-replay-event-types';

/**
 * True when any envelope in the batch is a session keyframe. Matched by the
 * stable string name, not a platform's numeric event type, because the envelope
 * type is already mapped at capture and that string is what the index stores.
 */
export function rynReplayBatchHoldsFullSnapshot(
  batch: IRynReplayEnvelope[]
): boolean {
  return batch.some(
    (envelope) => envelope.type === RYN_REPLAY_FULL_SNAPSHOT_EVENT_TYPE
  );
}

/**
 * Enforce the retry-batch cap without silently destroying the session keyframe.
 *
 * Eviction is NOT plain FIFO. A type-2 FullSnapshot is the keyframe every later
 * incremental is a delta against (see RynReplayBatcher: "Dropping it would leave
 * the whole session unreplayable"). Cap-trimming therefore:
 *   1. Prefer dropping the NEWEST non-snapshot batch (support cares most about
 *      recent deltas that still have a keyframe; an old incremental without its
 *      snapshot is already useless).
 *   2. Never drop a snapshot-bearing batch while a non-snapshot exists.
 *   3. If every batch holds a FullSnapshot and we are still over the cap
 *      (re-attach storm), drop the OLDEST snapshot so the newest keyframe for
 *      subsequent events is retained.
 */
export function trimRynReplayRetryQueue(
  queue: IRynReplayEnvelope[][],
  cap: number
): void {
  while (queue.length > cap) {
    let nonSnapshotIdx = -1;
    for (let i = queue.length - 1; i >= 0; i -= 1) {
      if (!rynReplayBatchHoldsFullSnapshot(queue[i])) {
        nonSnapshotIdx = i;
        break;
      }
    }
    if (nonSnapshotIdx >= 0) {
      queue.splice(nonSnapshotIdx, 1);
      continue;
    }
    // All remaining batches hold FullSnapshots. Cap still applies: drop the
    // oldest so a re-attach's newest keyframe wins.
    queue.shift();
  }
}
